/**
 * Video link helpers.
 *
 * Premium listings may attach one video tour, supplied as a YouTube, Vimeo or
 * any other online streaming link (or a direct video file). These helpers turn
 * a user-provided link into a safe, embeddable source for the detail page.
 */

export type VideoProvider = "youtube" | "vimeo" | "file" | "embed";

export interface ParsedVideo {
  provider: VideoProvider;
  /** URL to load inside an <iframe> (embed) or a <video> element (file). */
  src: string;
  /** How the source should be rendered. */
  kind: "iframe" | "video";
}

const VIDEO_FILE_PATTERN = /\.(mp4|webm|ogg|ogv|mov|m4v)(\?.*)?$/i;

/** Pull a YouTube video id out of the various URL shapes YouTube allows. */
function extractYouTubeId(url: URL): string | null {
  const host = url.hostname.replace(/^www\./, "");

  if (host === "youtu.be") {
    const [id] = url.pathname.split("/").filter(Boolean);
    return id ?? null;
  }

  if (host === "youtube.com" || host === "m.youtube.com" || host === "youtube-nocookie.com") {
    if (url.pathname === "/watch") {
      return url.searchParams.get("v");
    }
    const [first, second] = url.pathname.split("/").filter(Boolean);
    if (first === "embed" || first === "shorts" || first === "live" || first === "v") {
      return second ?? null;
    }
  }

  return null;
}

/** Pull a Vimeo video id out of a vimeo.com link. */
function extractVimeoId(url: URL): string | null {
  const host = url.hostname.replace(/^www\./, "");
  if (host !== "vimeo.com" && host !== "player.vimeo.com") return null;

  if (host === "player.vimeo.com") {
    const [, id] = url.pathname.split("/").filter(Boolean); // /video/<id>
    return id ?? null;
  }

  const [id] = url.pathname.split("/").filter(Boolean);
  return id && /^\d+$/.test(id) ? id : null;
}

/**
 * Parse a raw video link into an embeddable source.
 * Returns null when no usable link is provided.
 */
export function parseVideoUrl(raw?: string | null): ParsedVideo | null {
  if (!raw) return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") return null;

  const youtubeId = extractYouTubeId(url);
  if (youtubeId) {
    return {
      provider: "youtube",
      kind: "iframe",
      src: `https://www.youtube-nocookie.com/embed/${youtubeId}?rel=0&modestbranding=1&playsinline=1`,
    };
  }

  const vimeoId = extractVimeoId(url);
  if (vimeoId) {
    return {
      provider: "vimeo",
      kind: "iframe",
      src: `https://player.vimeo.com/video/${vimeoId}`,
    };
  }

  if (VIDEO_FILE_PATTERN.test(url.pathname)) {
    return { provider: "file", kind: "video", src: url.toString() };
  }

  // Best effort for other streaming providers that expose an embeddable page.
  return { provider: "embed", kind: "iframe", src: url.toString() };
}

/** True when the string is a plausible http(s) video link. */
export function isValidVideoLink(raw?: string | null): boolean {
  return parseVideoUrl(raw) !== null;
}